import { useMemo, useRef, useState } from 'react';
import { CalendarClock, CornerDownLeft, Inbox } from 'lucide-react';

import { cn } from '@/lib/cn';
import { parseQuickAdd } from '@/features/tasks/quickAddParser';
import { useClients, useProjects } from '@/features/tasks/useTasks';
import type { CreateTaskInput } from '@/features/tasks/useTasks';
import { captureIntent, describeIntent, type CaptureIntent } from './quickCapture';

interface Props {
  timezone: string;
  dayStartMinute?: number;
  submitting?: boolean;
  onCapture: (input: CreateTaskInput, intent: CaptureIntent) => void;
}

/** Token pills, matching the capture chips used on the backlog screen. */
const CHIP_TONE: Record<string, string> = {
  estimate: 'bg-surface-3 text-text-secondary',
  priority: 'bg-danger-quiet text-danger',
  client: 'bg-accent-quiet text-accent',
  project: 'bg-accent-quiet text-accent',
  energy: 'bg-surface-3 text-text-secondary',
  due: 'bg-warning-quiet text-warning',
  schedule: 'bg-accent-quiet text-accent',
};

/**
 * Capture, on the planning screen.
 *
 * A thought that arrives while planning is the one most likely to be lost,
 * stopping to open the backlog costs the plan you were holding. What is typed
 * goes to the backlog by default; a clock time sends it straight to the grid,
 * because "9am" is already a decision about when.
 */
export function PlanQuickAdd({ timezone, dayStartMinute, submitting, onCapture }: Props) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const clients = useClients();
  const projects = useProjects();

  const parsed = useMemo(
    () =>
      parseQuickAdd(input, {
        clients: clients.data ?? [],
        projects: projects.data ?? [],
      }),
    [input, clients.data, projects.data],
  );

  const intent = captureIntent(parsed, { timezone, dayStartMinute });
  const when = describeIntent(intent, timezone);
  const scheduling = intent.kind === 'schedule';
  const typed = input.trim().length > 0;
  const canSave = parsed.title.trim().length > 0;

  const submit = () => {
    if (!canSave) return;
    onCapture(
      {
        title: parsed.title,
        client_id: parsed.client_id ?? clients.data?.[0]?.id,
        project_id: parsed.project_id,
        estimate_minutes: parsed.estimate_minutes,
        ...(parsed.priority ? { priority: parsed.priority } : {}),
        ...(parsed.energy ? { energy: parsed.energy } : {}),
        // A bare time became a block, so it is a plan and not a deadline; an
        // explicit `>` keeps whatever due date was also given.
        due_at: intent.dueAt?.toISOString() ?? null,
      },
      intent,
    );
    setInput('');
    inputRef.current?.focus();
  };

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <label htmlFor="plan-capture" className="text-small font-medium text-text-secondary">
          Capture
        </label>
        {!typed && (
          <span className="text-label tabular text-text-tertiary">~45m @client &gt;9am</span>
        )}
      </div>

      {/* Input and its action are one control: a detached button beside a bare
          box reads as two unrelated things and wastes the narrow column. */}
      <div
        className={cn(
          'flex items-center rounded border bg-surface-1',
          'focus-within:border-accent-border',
          scheduling ? 'border-accent-border' : 'border-border',
        )}
      >
        <input
          id="plan-capture"
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
            if (e.key === 'Escape') setInput('');
            // The grid listens for single-key shortcuts; typing here is typing.
            e.stopPropagation();
          }}
          placeholder="Anything on your mind"
          className={cn(
            'min-h-8 min-w-0 flex-1 rounded bg-transparent px-2 py-1',
            'text-small text-text placeholder:text-text-tertiary outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!canSave || submitting}
          aria-label={scheduling ? 'Add and place on the grid' : 'Add to backlog'}
          className={cn(
            'pressable mr-0.5 flex size-control shrink-0 items-center justify-center rounded-sm',
            canSave
              ? 'bg-surface-2 text-text hover:bg-surface-3'
              : 'text-text-tertiary opacity-40',
          )}
        >
          <CornerDownLeft size={13} strokeWidth={1.5} aria-hidden />
        </button>
      </div>

      {/* Where it lands, said before Enter is pressed, the difference between
          a deadline and an appointment gets learned by watching it change. */}
      {typed && (
        <div className="mt-1.5 flex flex-col gap-1">
          <p
            className={cn(
              'flex items-center gap-1.5 text-label',
              scheduling ? 'text-accent' : 'text-text-secondary',
            )}
          >
            {scheduling ? (
              <CalendarClock size={12} strokeWidth={1.5} className="shrink-0" aria-hidden />
            ) : (
              <Inbox size={12} strokeWidth={1.5} className="shrink-0" aria-hidden />
            )}
            <span className="min-w-0 flex-1 truncate tabular" title={when ?? undefined}>
              {scheduling ? when : 'Backlog'}
            </span>
          </p>

          {parsed.tokens.length > 0 && (
            <ul className="flex flex-wrap gap-1">
              {parsed.tokens.map((token) => (
                <li
                  key={`${token.type}-${token.raw}`}
                  className={cn(
                    'rounded-sm px-1.5 py-0.5 text-label',
                    CHIP_TONE[token.type] ?? 'bg-surface-3 text-text-secondary',
                  )}
                >
                  {token.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
